
import { useState, useRef, useEffect, useCallback } from "react";
import { useToast } from "@/hooks/use-toast";
import { useTrainingProgress } from "@/hooks/useTrainingProgress";
import { useVideoProgress } from "@/hooks/useVideoProgress";

interface UseVideoPlaybackProps {
  url: string;
  fileId?: string;
  resourceId?: string;
  trackProgress?: boolean;
  onComplete?: () => void;
  onError?: (error: string) => void;
}

export function useVideoPlayback({
  url,
  fileId,
  resourceId,
  trackProgress = true,
  onComplete,
  onError
}: UseVideoPlaybackProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [retries, setRetries] = useState(0);
  const [loading, setLoading] = useState(true);
  const [hasCompleted, setHasCompleted] = useState<boolean>(false);
  const { toast } = useToast();
  const { markAsCompleted, isCompleted } = useTrainingProgress(resourceId, fileId);
  const { updateVideoProgress } = useVideoProgress({ resourceId, fileId });
  const completionHandledRef = useRef(false);
  const progressIntervalRef = useRef<number | null>(null);

  // Reset completion state when URL changes
  useEffect(() => {
    if (!url) {
      console.error('VideoViewer received empty or invalid URL');
      const errorMsg = 'Video source is missing or invalid';
      setError(errorMsg);
      setLoading(false);
      if (onError) onError(errorMsg);
      return;
    }
    
    console.log('VideoViewer initialized with URL:', url);
    setHasCompleted(false);
    completionHandledRef.current = false;
    
    // Clear any existing interval
    if (progressIntervalRef.current) {
      window.clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
  }, [url, onError]);

  // Set up loading timeout
  useEffect(() => {
    const loadTimeoutId = setTimeout(() => {
      if (loading && !error) {
        const errorMsg = "Video is taking too long to load. There might be a connection issue.";
        setError(errorMsg);
        if (onError) onError(errorMsg);
      }
    }, 12000); // 12 seconds timeout
    
    return () => clearTimeout(loadTimeoutId);
  }, [url, retries, loading, error, onError]);

  // Track video progress at regular intervals
  const setupProgressTracking = useCallback(() => {
    if (!trackProgress || !fileId || !resourceId) return;
    
    const video = videoRef.current;
    if (!video) return;
    
    // Clear any existing interval
    if (progressIntervalRef.current) {
      window.clearInterval(progressIntervalRef.current);
    }
    
    // Set up new interval to track progress every 5 seconds
    progressIntervalRef.current = window.setInterval(() => {
      if (video.paused || video.ended) return;
      
      const currentTime = video.currentTime;
      const duration = video.duration;
      
      if (isNaN(duration) || duration <= 0) return;
      
      updateVideoProgress(currentTime, duration);
    }, 5000);
    
    return () => {
      if (progressIntervalRef.current) {
        window.clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
    };
  }, [trackProgress, fileId, resourceId, updateVideoProgress]);

  // Set up video event listeners
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleEnded = async () => {
      console.log('Video playback completed:', url);
      
      if (hasCompleted || completionHandledRef.current) {
        console.log('Video already marked as completed, skipping');
        return;
      }
      
      setHasCompleted(true);
      completionHandledRef.current = true;
      
      if (trackProgress && fileId && resourceId && !isCompleted) {
        try {
          // Update final progress (100%)
          await updateVideoProgress(video.duration, video.duration);
          await markAsCompleted();
          console.log('Successfully marked video as completed');
        } catch (err) {
          console.error('Error marking video as completed:', err);
        }
      }
      
      if (onComplete) {
        console.log('Calling onComplete callback');
        onComplete();
      }
    };
    
    const handleError = (e: Event) => {
      console.error('Video error:', e);
      const errorMessage = video.error ? 
        `Error ${video.error.code}: ${video.error.message}` : 
        'Error loading video';
      setError(errorMessage);
      setLoading(false);
      
      if (onError) onError(errorMessage);
      
      if (errorMessage.includes('network') || errorMessage.includes('NETWORK') || 
          errorMessage.includes('fetch') || errorMessage.includes('MEDIA_ERR_SRC_NOT_SUPPORTED')) {
        toast({
          title: "Video Loading Error",
          description: "Network connection issue detected. Please check your internet connection.",
          variant: "destructive",
        });
      }
    };
    
    const handlePlay = () => {
      setupProgressTracking();
    };
    
    const handlePause = () => {
      // Update progress when video is paused
      if (trackProgress && !video.ended && video.currentTime > 0) {
        updateVideoProgress(video.currentTime, video.duration);
      }
      
      // Clear interval when paused
      if (progressIntervalRef.current) {
        window.clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
    };
    
    const handleLoadStart = () => setLoading(true);
    const handleCanPlay = () => {
      setLoading(false);
      setError(null);
    };
    
    // Reset error on retry or source change
    setError(null);
    
    // Set the video source if it has changed
    if (video.src !== url) {
      console.log('Setting video source to:', url);
      try {
        video.src = url;
        video.load();
      } catch (err) {
        console.error('Error setting video source:', err);
        const errorMsg = 'Failed to load video source';
        setError(errorMsg);
        setLoading(false);
        if (onError) onError(errorMsg);
      }
    }
    
    // Add event listeners
    video.addEventListener('ended', handleEnded);
    video.addEventListener('error', handleError);
    video.addEventListener('loadstart', handleLoadStart);
    video.addEventListener('canplay', handleCanPlay);
    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);
    
    return () => {
      // Clean up event listeners and intervals
      video.removeEventListener('ended', handleEnded);
      video.removeEventListener('error', handleError);
      video.removeEventListener('loadstart', handleLoadStart);
      video.removeEventListener('canplay', handleCanPlay);
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
      
      if (progressIntervalRef.current) {
        window.clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
    };
  }, [url, retries, toast, fileId, resourceId, markAsCompleted, trackProgress, hasCompleted, isCompleted, onComplete, onError, updateVideoProgress, setupProgressTracking]);

  const handleRetry = () => {
    setRetries(prev => prev + 1);
    setError(null);
    setLoading(true);
    completionHandledRef.current = false;
    
    const video = videoRef.current;
    if (video) {
      video.load();
      video.play().catch(err => {
        console.error('Error playing video after retry:', err);
      });
    }
  };

  return {
    videoRef,
    error,
    loading,
    retries,
    handleRetry
  };
}
